import React, { useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import SelectWithFetchableOptions from '../../../../../Form/SelectWithFetchableOptions';
import Button from '../../../../../Button';

import renderLinksList from '../../../../../../helpers/tables/renderLinksList';
import formatCurrency from '../../../../../../helpers/formatCurrency';
import LenderService from '../../../../../../services/LenderService';
import BillLineItemService from '../../../../../../services/BillLineItemService';
import { useUiContext } from '../../../../../../context/UiContext';
import sumDollarAmounts from '../../../../../../helpers/sumDollarAmounts';

const calculateBillPaymentsTotal = (lineItems) => {
  if (!lineItems || !lineItems.length) {
    return 0;
  }

  const totalPerItem = [];

  lineItems.forEach((lineItem) => {
    const total = sumDollarAmounts(
      lineItem._bill_payment_bill_line_items,
      'amount'
    );

    totalPerItem.push({ total });
  });

  const totalAllItem = sumDollarAmounts(totalPerItem, 'total');

  return totalAllItem;
};

export default function BillPaymentsCol({
  row,
  bill,
  loanId,
  refetch,
  lineItems,
  isLender,
}) {
  const [currentOpenBillPaymentId, setCurrentOpenBillPaymentId] =
    useState(null);

  const isTotal = row.id === 'total';

  const { showDialogModal, showToast } = useUiContext();

  const { mutate: addBillLineItemToBillPayment } = useMutation({
    mutationFn: BillLineItemService.addBillLineItemToBillPayment,
    onSuccess: () => {
      showToast({
        text: 'Bill line item added to bill payment successfully',
        type: 'success',
      });

      refetch();
    },
    onError: (error) => {
      showToast({
        text: error?.response?.data?.message || 'Something went wrong',
        type: 'error',
      });
    },
  });

  if (isTotal) {
    const total = calculateBillPaymentsTotal(lineItems);
    return `$${formatCurrency(total)}`;
  }

  if (
    !!bill?.lender_id &&
    (!row._bill_payment_bill_line_items ||
      !row._bill_payment_bill_line_items.length) &&
    !isLender
  ) {
    return (
      <div className="flex gap-4 items-end">
        <SelectWithFetchableOptions
          fixedPosition
          value={currentOpenBillPaymentId}
          setFieldValue={(name, value) => setCurrentOpenBillPaymentId(value)}
          fetchOptions={{
            key: ['lenderOpenBillPayments', bill.lender_id],
            disabled: !bill.lender_id,
            labelKey: 'pixel_number',
            queryFn: () =>
              LenderService.getLenderOpenBillPayments(bill.lender_id),
          }}
        />
        <Button
          className="h-[37px]"
          variant="primary"
          disabled={!currentOpenBillPaymentId}
          onClick={() => {
            showDialogModal({
              title: 'Confirmation',
              message:
                'Are you sure you want to add this bill line item to this bill payment?',
              onConfirm: () =>
                addBillLineItemToBillPayment({
                  billLineItemId: row.id,
                  billPaymentId: currentOpenBillPaymentId,
                }),
              confirmButtonLabel: 'Yes, add it',
              cancelButtonLabel: 'No, cancel',
            });
          }}
        >
          Add to
        </Button>
      </div>
    );
  }

  return renderLinksList({
    data: row._bill_payment_bill_line_items,
    createLabel: (item) => {
      const pixelNumber = item._bill_payment?.pixel_number || '';
      const amount = item.amount ? `$${formatCurrency(item.amount)}` : '';

      return `${pixelNumber}${amount ? ` - ${amount}` : ''}`;
    },
    createLink: (item) =>
      `/loans/${loanId}/bill-payments/${item.bill_payment_id}?tab=Bill Payment`,
  });
}
